import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../../lib/utils.js';

type Variant = 'primary' | 'outline' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  children: ReactNode;
}

const variantStyles: Record<Variant, string> = {
  primary:
    'bg-brand-500 text-white border border-brand-500 hover:bg-brand-600 hover:border-brand-600 disabled:bg-brand-300 disabled:border-brand-300',
  outline:
    'bg-white text-n-700 border border-n-200 hover:bg-n-50 hover:border-n-300 disabled:bg-n-50 disabled:text-n-400',
  ghost:
    'bg-transparent text-n-700 border border-transparent hover:bg-n-100 disabled:text-n-400',
  danger:
    'bg-danger-500 text-white border border-danger-500 hover:bg-danger-600 hover:border-danger-600 disabled:bg-danger-300 disabled:border-danger-300',
};

const sizeStyles: Record<Size, string> = {
  sm: 'h-[28px] px-2.5 text-[12px] gap-1.5 [&>svg]:h-[12px] [&>svg]:w-[12px]',
  md: 'h-[32px] px-3 text-[13px] gap-2 [&>svg]:h-[13px] [&>svg]:w-[13px]',
  lg: 'h-[40px] px-4 text-[14px] gap-2 [&>svg]:h-[16px] [&>svg]:w-[16px]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', loading, disabled, className, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cn(
          'inline-flex items-center justify-center rounded-md font-medium transition-colors',
          'disabled:cursor-not-allowed',
          variantStyles[variant],
          sizeStyles[size],
          className,
        )}
        {...props}
      >
        {loading ? (
          <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : null}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';
